/**
 * Three India-specific "leak buckets" mapped onto existing categories / merchants.
 */

export const LEAK_BUCKETS = [
    {
        id: 'dining',
        label: 'Dining & Delivery',
        shortLabel: 'Dining',
        description: 'Swiggy, Zomato, cafes',
        defaultLimit: 5000,
        categories: ['Food & Drinks'],
        merchantHints: ['swiggy', 'zomato', 'dominos', 'mcdonald', 'kfc', 'burger king', 'cafe'],
    },
    {
        id: 'qcommerce',
        label: 'Quick Commerce & Groceries',
        shortLabel: 'Quick Commerce',
        description: 'Blinkit, Zepto, Instamart',
        defaultLimit: 4000,
        categories: ['Groceries'],
        merchantHints: ['blinkit', 'zepto', 'instamart', 'bbnow', 'bb now', 'bigbasket', 'dunzo'],
        subcategories: ['Zepto', 'Blinkit', 'Instamart'],
    },
    {
        id: 'transit',
        label: 'Transit & Commute',
        shortLabel: 'Transit',
        description: 'Uber, Ola, Rapido',
        defaultLimit: 3000,
        categories: ['Transport'],
        merchantHints: ['uber', 'ola', 'rapido', 'namma yatri', 'nammayatri', 'metro', 'irctc'],
    },
];

const BUDGET_KEY_PREFIX = 'leak:';

export function budgetCategoryKey(bucketId) {
    return `${BUDGET_KEY_PREFIX}${bucketId}`;
}

export function isLeakBudgetKey(category) {
    return typeof category === 'string' && category.startsWith(BUDGET_KEY_PREFIX);
}

function parseAmount(str) {
    if (typeof str === 'number') return Math.abs(str);
    if (!str) return 0;
    return Math.abs(parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0);
}

function isDebit(txn) {
    return txn.type && String(txn.type).toUpperCase() === 'DEBIT';
}

function matchesBucket(txn, bucket) {
    const party = `${txn.party || ''} ${txn.raw_merchant || ''} ${txn.merchant_key || ''}`.toLowerCase();
    const sub = (txn.subcategory || '').toLowerCase();

    if (bucket.merchantHints.some(h => party.includes(h))) return true;
    if (bucket.subcategories?.some(s => sub === s.toLowerCase() || party.includes(s.toLowerCase()))) return true;
    if (bucket.categories.includes(txn.category)) {
        // For groceries bucket, prefer q-commerce merchants; still count grocery category
        if (bucket.id === 'qcommerce') return true;
        if (bucket.id === 'dining') return true;
        if (bucket.id === 'transit') return true;
    }
    return false;
}

/**
 * Sum debit spend per leak bucket for the given transactions (already month-filtered preferred).
 */
export function getLeakBucketSpending(transactions) {
    const result = {};
    for (const bucket of LEAK_BUCKETS) {
        result[bucket.id] = { spent: 0, count: 0, merchants: {} };
    }

    for (const txn of transactions) {
        if (!isDebit(txn)) continue;
        const amount = parseAmount(txn.amount);
        if (amount <= 0) continue;

        for (const bucket of LEAK_BUCKETS) {
            if (matchesBucket(txn, bucket)) {
                result[bucket.id].spent += amount;
                result[bucket.id].count += 1;
                const key = txn.party || 'Unknown';
                result[bucket.id].merchants[key] = (result[bucket.id].merchants[key] || 0) + amount;
                break; // assign to first matching bucket only
            }
        }
    }

    return result;
}

/**
 * Top leak bucket by spend.
 */
export function getTopLeak(transactions) {
    const spending = getLeakBucketSpending(transactions);
    let top = null;
    for (const bucket of LEAK_BUCKETS) {
        const spent = spending[bucket.id].spent;
        if (!top || spent > top.spent) {
            top = { bucket, spent, count: spending[bucket.id].count };
        }
    }
    return top;
}

/**
 * Mid-month projection vs limit.
 * @returns {{ spent, limit, projected, exceedBy, daysElapsed, daysInMonth, onTrack }}
 */
export function projectBucketPace(spent, limit, now = new Date()) {
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysElapsed = Math.max(1, now.getDate());
    const projected = (spent / daysElapsed) * daysInMonth;
    const exceedBy = Math.max(0, Math.round(projected - limit));
    return {
        spent,
        limit,
        projected: Math.round(projected),
        exceedBy,
        daysElapsed,
        daysInMonth,
        onTrack: projected <= limit,
    };
}

export function formatINR(n) {
    const num = Math.round(Number(n) || 0);
    return '₹' + num.toLocaleString('en-IN');
}
